import { ReactElement, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Formik, FormikHelpers } from 'formik'
import { Amplify } from 'aws-amplify'
import { resetPassword } from '@aws-amplify/auth'
import { Button } from '../../components/atoms'
import logo from '../../assets/images/logo.svg'
import { Modal } from '../../components/molecules'
import { toast } from 'react-toastify'
import ControlHighlight from '../../components/molecules/KeyboardControls/ControlHighlight'
import { useKeyboardControls } from '../../contexts/KeyboardControlsContext'
import KeyboardControl from '../../components/molecules/KeyboardControls/KeyboardControl'
import { HighlightEffect } from '../../enums/KeyboardControls'
import ContactLinks from '../../components/molecules/ContactLinks/ContactLinks'

export enum FocusableElement {
  ButtonResend = 'button-resend',
  LinkContact = 'link-contact',
}

function PasswordReset(): ReactElement {
  const navigate = useNavigate()
  const location = useLocation()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const keyboardControls = useKeyboardControls()
  
  // Get email from location state or redirect to welcome page
  const email = location.state?.email
  if (!email) {
    navigate('/')
  }

  const handleResendEmail = async (): Promise<void> => {
    try {
      setIsSubmitting(true)
      const currentConfig = Amplify.getConfig()
      console.log('Full Amplify configuration:', currentConfig)
      const result = await resetPassword({ username: email })
      console.log('Password reset request result:', result)
      toast.success('Password reset email sent successfully', { position: toast.POSITION.TOP_CENTER })
    } catch (err: any) {
      console.error('Error resending password reset email:', err)
      toast.error('Failed to resend password reset email. Please try again.', { position: toast.POSITION.TOP_CENTER })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className='bg-black min-h-screen font-primary items-center flex justify-center'>
      <header className='absolute left-[40px] top-[40px]' aria-hidden='true'>
        <img src={logo} className='w-[138px]' alt='logo' />
      </header>
      <section>
        <Formik
          initialValues={{}}
          onSubmit={async (_values, actions: FormikHelpers<{}>): Promise<void> => {
            await handleResendEmail()
            actions.setSubmitting(false)
          }}
        >
          {() => (
            <Modal tall>
              <h1 className='sr-only'>Password Reset - Check your email</h1>
              <h2 className='font-bold capitalize text-[26px] text-center'>
                password reset
              </h2>
              <p className='pt-14 pb-16 font-bold text-[38px] leading-none min-h-1024:pt-20 min-h-1024:pb-32'>
                Check your email
              </p>
              <p className='text-[26px] w-[430px] leading-[3rem] font-thin text-center'>
              Click the button in the email sent to{' '}
                <span className='text-dopage-darkGreen'>{email}</span>
                <br />
                to reset your passowrd.
                <br />
                <br />
                <br />
              </p>
              <div className='max-w-[440px] w-full min-h-1024:mt-[6.5rem]'>
                <ControlHighlight
                  active={keyboardControls.isEnabled && keyboardControls.isElementFocused(FocusableElement.ButtonResend)}
                  highlightEffect={HighlightEffect.ButtonHexOutline}
                  data-testid={`highlight-${FocusableElement.ButtonResend}`}
                >
                  <KeyboardControl>
                    <Button
                      className='font-primary bg-dopage-primary w-full h-[86px] diamond-btn font-bold mt-[40px] flex justify-center items-center'
                      data-testid={FocusableElement.ButtonResend}
                      disabled={isSubmitting}
                      id={FocusableElement.ButtonResend}
                      loading={isSubmitting}
                      onClick={handleResendEmail}
                    >
                      <span className='text-[26px] text-neutral-090'>
                        Resend Email
                      </span>
                    </Button>
                  </KeyboardControl>
                </ControlHighlight>
                <ContactLinks
                  id={FocusableElement.LinkContact}
                />
              </div>
            </Modal>
          )}
        </Formik>
      </section>
    </div>
  )
}

export default PasswordReset
